import React, { useMemo, useState } from "react";
import {
  ActionDialog,
  Button,
  Checkbox,
  CustomDropdown,
  type DropdownOption,
} from "../../../components/ui";
import { usePlans } from "../../brand/hooks/usePlans";
import { formatPrice } from "../../brand/planFormat";
import type { NewEnquiryPayment } from "../types/enquiries.types";

/**
 * The two screens the dialog is: the form that builds the request, and the
 * read-back that has to be confirmed before it goes. One dialog rather than
 * two, so the second is plainly the same act as the first — and going back
 * lands on a form still holding what was typed into it.
 */
type Step = "form" | "confirm";

export interface RequestPaymentModalProps {
  /** Who the request is for, salutation and all: "Mr. Subham Patil". */
  recipient: string;
  /** Their address, or empty where there is none on record. */
  email: string;
  onClose: () => void;
  /** Sends the request. Closing the dialog after it is the caller's. */
  onConfirm: (payment: NewEnquiryPayment) => void;
  /**
   * Set while the caller's request is in flight, for the callers whose
   * `onConfirm` goes to the network: the button spins and stops taking
   * presses, so one request cannot be raised twice by an impatient second
   * click. Left off, the dialog behaves as it always has.
   */
  busy?: boolean;
}

/**
 * Requests payment for one of the workspace's plans — from an enquiry, or
 * from a client the Payments tab is raising a fresh request against. It takes
 * a name and an address rather than either record, because the two screens
 * hold different ones and the dialog only ever reads those two fields.
 *
 * The price is the plan's, not something typed: what is being sold is a
 * listed plan, so the amount is read off the catalogue and only lowered —
 * a discount — by ticking for it. That is also why the left-hand box never
 * becomes editable; it is what the plan costs, shown so the discount beside
 * it can be read against something.
 */
const RequestPaymentModal: React.FC<RequestPaymentModalProps> = ({
  recipient,
  email,
  onClose,
  onConfirm,
  busy = false,
}) => {
  // Fetched when the dialog opens, which is the only time the list is needed.
  const { plans, status } = usePlans();

  const [step, setStep] = useState<Step>("form");
  const [planId, setPlanId] = useState("");
  const [discounting, setDiscounting] = useState(false);
  // Kept as a string while it is being typed, so the box can be emptied. Only
  // digits are stored — the ₹ lives in the field, never in the value.
  const [discountAmount, setDiscountAmount] = useState("");

  // A plan that is not listed cannot be sold: an in-active one is off the
  // public page, and putting it on a payment request would sell it anyway.
  const sellable = useMemo(
    () => plans.filter((plan) => plan.status === "Active"),
    [plans]
  );

  const options: DropdownOption[] = useMemo(
    () =>
      sellable.map((plan) => ({
        value: plan.id,
        // Name and duration together, because a workspace sells the same
        // plan at several lengths and the name alone picks none of them.
        label: `${plan.name} | ${plan.duration}`,
        subtitle: formatPrice(plan.price),
      })),
    [sellable]
  );

  const plan = sellable.find((one) => one.id === planId);

  /** Both boxes follow the plan, so a new pick re-prices the request. */
  const handlePlanChange = (id: string) => {
    setPlanId(id);
    const picked = sellable.find((one) => one.id === id);
    setDiscountAmount(picked ? String(picked.price) : "");
  };

  /**
   * Ticking empties the box to be typed into: it was showing the list price,
   * and a discount left at that is not a discount — so the box opens waiting
   * for a figure rather than pre-filled with the one figure it cannot keep.
   *
   * Un-ticking puts the list price back, since there is no discount to keep.
   */
  const handleDiscountToggle = (checked: boolean) => {
    setDiscounting(checked);
    setDiscountAmount(checked ? "" : plan ? String(plan.price) : "");
  };

  // What is actually being asked for. Without the tick it is the list price,
  // whatever the box was last left showing.
  const amount = discounting ? Number(discountAmount) : (plan?.price ?? 0);

  /**
   * What is wrong with the amount being charged, where the tick says a
   * discount is being given. Nothing to say until it is: without the tick the
   * box is the list price, which cannot be wrong.
   *
   * Both problems are the same mistake in two directions — a box that does not
   * hold a discount — so they are one message slot rather than two, and the
   * field is refused for either.
   */
  const discountError = ((): string | undefined => {
    if (!plan || !discounting) return undefined;

    // A "discount" above the list price is a rise, and nothing on this dialog
    // is for raising a price — that is an edit to the plan itself.
    if (amount > plan.price) {
      return `A discount cannot be more than the plan price of ${formatPrice(plan.price)}.`;
    }

    // And one equal to it is no discount at all. The tick says money is coming
    // off; a box still showing the list price is one nobody finished filling,
    // and letting it through would send a "discounted" request at full price.
    if (amount === plan.price) {
      return `That is the plan price. Lower it, or clear the tick to charge the full ${formatPrice(plan.price)}.`;
    }

    return undefined;
  })();

  const valid = !!plan && amount > 0 && !discountError;

  const handleRequest = () => {
    if (!valid) return;
    setStep("confirm");
  };

  const handleConfirm = () => {
    if (!plan || !valid || busy) return;
    onConfirm({
      planId: plan.id,
      planName: plan.name,
      duration: plan.duration,
      price: plan.price,
      amount,
    });
  };

  return (
    <ActionDialog
      title="Create new payment"
      onClose={onClose}
      className="max-w-[526px]"
      // Not while the raise is with the server: the request is going out
      // either way, and a panel dismissed over it would leave nothing on
      // screen to say so.
      dismissable={!busy}
      footer={
        step === "form" ? (
          <>
            <Button variant="secondary" size="md" onClick={onClose}>
              Cancel
            </Button>
            <Button size="md" disabled={!valid} onClick={handleRequest}>
              Request payment
            </Button>
          </>
        ) : (
          <>
            {/* Back rather than Cancel: the way out of a read-back is the form
                it was read off, with everything still in it. */}
            <Button
              variant="secondary"
              size="md"
              disabled={busy}
              onClick={() => setStep("form")}
            >
              Back
            </Button>
            <Button size="md" loading={busy} onClick={handleConfirm}>
              Confirm
            </Button>
          </>
        )
      }
    >
      {step === "form" ? (
        <div>
          <CustomDropdown
            options={options}
            value={planId}
            onChange={handlePlanChange}
            aria-label="Select plan"
            placeholder={
              status === "failed"
                ? "Could not load your plans"
                : "Select plan"
            }
            // Nothing to pick from until the list is in, and nothing to pick
            // from at all in a workspace that has listed no plan yet.
            disabled={status !== "ready" || sellable.length === 0}
            searchable={sellable.length > 8}
          />
          {status === "ready" && sellable.length === 0 && (
            <p className="mt-1.5 text-[12px] text-ink-muted">
              Add an active plan under My brand to request payment for it.
            </p>
          )}

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PriceField
              label="Plan price"
              value={plan ? String(plan.price) : ""}
              readOnly
            />
            <PriceField
              label="Amount to charge"
              value={discountAmount}
              // The box only opens once the tick says a discount is being
              // given; until then it shows the price rather than takes one.
              readOnly={!discounting}
              invalid={!!discountError}
              onValueChange={setDiscountAmount}
            />
          </div>

          <Checkbox
            className="mt-2"
            label="Add discount price"
            checked={discounting}
            onCheckedChange={handleDiscountToggle}
          />

          {discountError && (
            <p role="alert" className="mt-2 text-[12px] text-red-500">
              {discountError}
            </p>
          )}
        </div>
      ) : (
        <div>
          <p className="text-[14px] 3xl:text-[15px] text-ink">
            Send a payment request of{" "}
            <span className="font-medium">{formatPrice(amount)}</span> to{" "}
            <span className="font-medium">{recipient}</span>
            {email ? (
              <>
                {" "}
                at <span className="font-medium">{email}</span>
              </>
            ) : null}
            ?
          </p>

          {/* Label, colon and value in three columns, so the values line up
              however long the labels get — the same read-back the Send plan
              dialog uses. */}
          <dl className="mt-4 grid grid-cols-[130px_10px_1fr] gap-y-3 rounded-md border border-line bg-[#F9FBFC] px-4 py-4 text-[14px]">
            <Row label="Plan" value={plan?.name ?? "-"} />
            <Row label="Duration" value={plan?.duration ?? "-"} />
            <Row
              label="Plan price"
              value={formatPrice(plan?.price ?? 0)}
            />
            {discounting && (
              <Row label="Discounted price" value={formatPrice(amount)} />
            )}
          </dl>

          <p className="mt-4 text-[13px] text-ink-muted">
            {email
              ? "A secure payment link will be emailed as soon as this is confirmed."
              : "There is no email on record here, so the link will have to be shared by hand."}
          </p>
        </div>
      )}
    </ActionDialog>
  );
};

/** One line of the read-back. */
const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <>
    <dt className="text-ink-muted">{label}</dt>
    <dd aria-hidden="true" className="text-ink-muted">
      :
    </dd>
    <dd className="text-ink">{value}</dd>
  </>
);

/**
 * A price box with the ₹ inside it. Read-only rather than `disabled`, so the
 * amount can still be selected and copied out of a box that cannot be typed
 * into — and so a screen reader reads it rather than skipping it.
 */
const PriceField: React.FC<{
  /** Names the box to a screen reader; the design has no visible labels here. */
  label: string;
  value: string;
  readOnly?: boolean;
  invalid?: boolean;
  onValueChange?: (value: string) => void;
}> = ({ label, value, readOnly = false, invalid = false, onValueChange }) => (
  <div className="relative">
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] 3xl:text-[15px] text-ink-subtle"
    >
      ₹
    </span>
    <input
      // `inputMode` keeps the numeric keypad on mobile; a `number` input would
      // add spinners the design doesn't have.
      type="text"
      inputMode="numeric"
      aria-label={label}
      value={value}
      readOnly={readOnly}
      aria-invalid={invalid || undefined}
      placeholder="499"
      onChange={(event) =>
        onValueChange?.(event.target.value.replace(/[^0-9]/g, ""))
      }
      className={`w-full rounded-[6px] border py-2.5 3xl:py-3 pl-8 pr-3.5 text-[14px] 3xl:text-[15px] placeholder:text-ink-subtle focus:outline-none transition-colors ${
        // One expression rather than two stacked conditionals: a box cannot be
        // both read-only and refused, and two of them would put a border
        // colour from either state in the same class list.
        readOnly
          ? "border-line bg-[#F7F9FA] text-ink-muted"
          : invalid
            ? "bg-white text-ink border-red-400 focus:border-red-500"
            : "bg-white text-ink border-line focus:border-brand"
      }`}
    />
  </div>
);

export default RequestPaymentModal;
